<template>
  <ion-page id="mission">
    <WhiteHeader
      :back-button="!isOutsourcing"
      route-back="/planning"
      title="Détails d'une mission"
    />
    <ion-content class="ion-padding">
      <ion-refresher
        class="refresher-detail refresh-detail-worksite"
        slot="fixed"
        @ionRefresh="doRefresh($event)"
        @click="doRefresh($event)"
        ><ion-refresher-content></ion-refresher-content
      ></ion-refresher>

      <liste-rental-coupon
        v-if="hasCoupons"
        :rental-coupons="missionDetail.rental_coupons"
        type-mission="reservation"
      />

      <DayPart :part="mission.day_part"></DayPart>
      <Date
        v-if="humanStart"
        :start="humanStart"
        :end="humanEnd"
        :time-slot="mission.time_slot"
      />
      <hr class="color-hr" />

      <Equipment
        :machine_name="mission.machine_name"
        :machine_category="mission.machine_category"
        :machine_number="mission.machine_number"
        :machine_translation="mission.machine_translation"
        :machine_poids="mission.machine_poids"
        :tools="mission.tools"
        :attachments="mission.machine_attachments"
        :mission-id="mission.id_reservation"
        :dump-body="mission.dump_body"
        :dump-bodies="mission.dump_bodies"
        @refresh-mission="doRefresh"
        :display-dump-body-part="mission.display_dump_body_part"
        :disable-change-dump-body-message="disableChangeDumpBodyMessage"
      />

      <hr class="color-hr" />

      <information
        :name-company="mission.builder"
        :manager="missionDetail.manager"
        :manager_phone="missionDetail.manager_phone"
        :manager2="missionDetail.manager2"
        :manager2_phone="missionDetail.manager2_phone"
        :worksite_city="mission.worksite_city"
        :worksite_name="mission.worksite_name"
        :worksite_address="mission.worksite_address"
        :worksite_manual="mission.worksite_manual"
        :worksite_coordinates="mission.worksite_coordinates"
        :instructions="mission.instructions"
        :order_number="mission.order_number"
        :worksite_reference="mission.worksite_reference"
        :isReservation="true"
        :attachments="mission.attachments_with_categories"
        :mission-id="mission.id_reservation"
        ref="worksiteMap"
      />
      <hr class="color-hr" />

      <new-rental-coupon
        v-if="mission.pricing_information"
        type-mission="reservation"
        :has-coupons="hasCoupons"
        :has-questions="!isOutsourcing && mission.has_questions"
      />
    </ion-content>
  </ion-page>
</template>

<script>
import {
  IonPage,
  IonContent,
  IonRefresher,
  IonRefresherContent,
  toastController,
} from '@ionic/vue';
import {
  forceRefreshDetailMission,
  forceRefreshPlanning,
  getDetailMission,
  getMissionOrder,
  fetchMissionOutsourcing,
  fetchDetailMissionOutsourcing,
} from '@/services/driver/missionsOrders';
import Equipment from '@/components/driver/detailMission/Equipment';
import Information from '@/components/driver/detailMission/Information';
import Date from '@/components/driver/detailMission/Date';
import DayPart from '@/components/driver/card/detailCardPlanning/DayPart.vue';
import NewRentalCoupon from '@/components/driver/detailMission/NewRentalCoupon';
import ListeRentalCoupon from '@/components/driver/detailMission/ListeRentalCoupon';
import WhiteHeader from '@/components/driver/header/WhiteHeader';
import HasMissionDetailMixin from '@/mixins/HasMissionDetailMixin';

export default {
  name: 'WorksiteDetail',
  components: {
    WhiteHeader,
    ListeRentalCoupon,
    NewRentalCoupon,
    Date,
    DayPart,
    Information,
    Equipment,
    IonPage,
    IonContent,
    IonRefresher,
    IonRefresherContent,
  },
  mixins: [HasMissionDetailMixin],
  data() {
    return {
      mission: {},
      missionDetail: {},
      refreshInterval: null,
    };
  },

  // Launch auto-refresh when page gets displayed
  ionViewDidEnter() {
    this.doRefresh();

    if (!this.isOutsourcing) {
      this.refreshInterval = setInterval(() => {
        this.doRefresh();
      }, 60 * 1000);
    }

    setTimeout(() => {
      this.initMaps();
    }, 0);
  },

  ionViewWillLeave() {
    if (this.$refs.worksiteMap) {
      this.$refs.worksiteMap.removeMap();
    }
  },

  // Stop auto-refresh when page quits
  ionViewDidLeave() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  },

  mounted() {
    Promise.all([
      this.getMissionRequest(),
      this.getDetailMissionRequest(),
    ]).then(([mission, missionDetail]) => {
      this.afterFetches(mission, missionDetail);
    });
  },

  methods: {
    getMissionRequest() {
      if (this.isOutsourcing) {
        return fetchMissionOutsourcing(
          'reservation',
          this.$route.params.outsourcingToken,
        );
      }

      return getMissionOrder(
        this.$route.params.id,
        this.$route.params.idRide,
        'reservation',
      );
    },

    getDetailMissionRequest(forceRefresh = false) {
      if (this.isOutsourcing) {
        return fetchDetailMissionOutsourcing(
          'reservation',
          this.$route.params.outsourcingToken,
        );
      }

      if (forceRefresh) {
        return forceRefreshDetailMission(
          'reservation',
          this.$route.params.id,
          null,
        );
      }

      return getDetailMission('reservation', this.$route.params.id, null);
    },

    doRefresh(event) {
      if (!this.isOutsourcing) {
        try {
          forceRefreshPlanning();
        } catch (error) {
          console.log('[WorksiteDetail] Unable to force-refresh planning.');
        }

        if (!this.$route || !this.$route.params || !this.$route.params.id)
          return;
      }

      Promise.all([
        this.getMissionRequest(),
        this.getDetailMissionRequest(true),
      ])
        .then(([mission, missionDetail]) => {
          this.afterFetches(mission, missionDetail);
        })
        .catch(() => {
          console.log('Unable to force-refresh worksite detail.');
        })
        .finally(() => {
          if (event && event.target && event.target.complete) {
            event.target.complete();
          }
        });
    },

    async backPlanning() {
      const toast = await toastController.create({
        header: 'Changement',
        message: 'La mission ne vous est plus affectée',
        duration: 3000,
        color: 'success',
        position: 'top',
        cssClass: 'toast-rental-coupon',
      });
      await toast.present();

      this.$router.push('/planning');
    },

    afterFetches(mission, missionDetail) {
      if (!mission) {
        this.backPlanning();
        return;
      }

      this.mission = mission;
      this.missionDetail = missionDetail;

      // Init map just in case
      this.$nextTick(() => {
        this.initMaps();
      });
    },

    initMaps() {
      if (this.$refs.worksiteMap) {
        this.$refs.worksiteMap.initMap();
      }
    },
  },
};
</script>
